import type { RoomTournamentListParams, TournamentListItem, TournamentStaffDetail } from '@brush/api-client';
import type { TournamentFilter } from './tournamentFilter';

/**
 * The tournament screen's rules over the room's list (`GET /api/rooms/{roomId}/tournaments`), pure and
 * importing only types so `node --test` drives them directly (`tournamentList.test.js`).
 */

/**
 * The tournament the detail view shows (gate answer Q11): its row from the page at once, and its own
 * record — results and sittings, which the page no longer carries — once that arrives. The record wins
 * when it is there, being the newer and the whole. A tournament an edit has just moved off the page is
 * still shown, from its record; with neither, there is nothing to show yet.
 */
export function openedTournament(
  rows: readonly TournamentListItem[],
  selectedId: string | null,
  full: TournamentStaffDetail | undefined,
): { row: TournamentListItem; full: TournamentStaffDetail | null } | null {
  if (selectedId === null) return null;
  if (full && full.id === selectedId) return { row: full, full };
  const row = rows.find((r) => r.id === selectedId);
  return row ? { row, full: null } : null;
}

/**
 * Whether an opened tournament's standings can be drawn: once its own read has arrived, since that read
 * names each finisher (performance rank 2). Until then they named players from the room's roster and had
 * to wait for that too — drawn before it, every row read "Unknown player".
 */
export function standingsState(input: {
  full: TournamentStaffDetail | null;
  resultsFailed: boolean;
}): 'loading' | 'failed' | 'ready' {
  if (input.resultsFailed) return 'failed';
  return input.full === null ? 'loading' : 'ready';
}

/**
 * What a bulk tag apply selects: the rows the list counted. The apply takes dates, not a preset, so it
 * takes the dates the page says it applied — the server's, at the room's day — and is not offered until a
 * page has said so. Worked out here instead, the two would pick different months for an evening each
 * month and the button would tag rows nobody was looking at.
 */
export function bulkTagSelection(
  filter: TournamentFilter,
  page: { from?: string | null; to?: string | null } | undefined,
): { from?: string; to?: string; tags?: string[]; excludeTags?: string[] } | null {
  if (!page) return null;
  return {
    from: page.from ?? undefined,
    to: page.to ?? undefined,
    tags: filter.tags.length > 0 ? filter.tags : undefined,
    excludeTags: filter.excludeTags.length > 0 ? filter.excludeTags : undefined,
  };
}

/**
 * The request for one page. A date preset goes by its name and the server resolves it at the room's
 * gaming day (gate answer Q4, ADR 0014), then says which dates it applied: the browser's calendar is a
 * day ahead every evening west of Greenwich, and on a month's last evening that made "this month" the
 * next one. A custom range is dates the operator typed, and goes as they are.
 */
export function toRoomListParams(filter: TournamentFilter, page: number): RoomTournamentListParams {
  const { preset, custom } = filter.date;
  const range = preset !== null ? { preset } : { from: custom.start ?? undefined, to: custom.end ?? undefined };
  return {
    ...range,
    tags: filter.tags.length > 0 ? filter.tags : undefined,
    excludeTags: filter.excludeTags.length > 0 ? filter.excludeTags : undefined,
    page,
    pageSize: filter.pageSize,
  };
}
